import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { FileText, Download } from 'lucide-react';

interface RESPAComplianceFormProps {
  dealId: string;
  customerId?: string;
  salesDate?: string;
}

interface RESPAData {
  originated_loan: boolean;
  current_year: number;
  previous_year_count: number;
  previous_year_high_interest: number;
  current_year_count: number;
  current_year_high_interest: number;
  signed_date: string;
}

const RESPAComplianceForm = ({ dealId, customerId, salesDate }: RESPAComplianceFormProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [existingId, setExistingId] = useState<string | null>(null);
  const currentYear = salesDate ? new Date(salesDate).getFullYear() : new Date().getFullYear();
  const [formData, setFormData] = useState<RESPAData>({
    originated_loan: false,
    current_year: currentYear,
    previous_year_count: 0,
    previous_year_high_interest: 0,
    current_year_count: 0,
    current_year_high_interest: 0,
    signed_date: '',
  });

  useEffect(() => {
    if (salesDate) {
      const year = new Date(salesDate).getFullYear();
      setFormData(prev => ({
        ...prev,
        current_year: year
      }));
    }
  }, [salesDate]);

  useEffect(() => {
    loadExistingData();
  }, [dealId]);

  const loadExistingData = async () => {
    try {
      const { data, error } = await supabase
        .from('respa_compliance')
        .select('*')
        .eq('deal_id', dealId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setExistingId(data.id);
        setFormData({
          originated_loan: data.originated_loan,
          current_year: data.year,
          previous_year_count: data.notes_originated_count || 0,
          previous_year_high_interest: data.notes_high_interest_count || 0,
          current_year_count: 0,
          current_year_high_interest: 0,
          signed_date: data.signed_date || '',
        });
      }
    } catch (error) {
      console.error('Error loading RESPA data:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const dataToSave = {
        deal_id: dealId,
        customer_id: customerId,
        originated_loan: formData.originated_loan,
        year: formData.current_year,
        notes_originated_count: formData.previous_year_count + formData.current_year_count,
        notes_high_interest_count: formData.previous_year_high_interest + formData.current_year_high_interest,
        signed_date: formData.signed_date || null,
      };

      if (existingId) {
        const { error } = await supabase
          .from('respa_compliance')
          .update(dataToSave)
          .eq('id', existingId);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('respa_compliance')
          .insert([dataToSave])
          .select()
          .single();

        if (error) throw error;
        setExistingId(data.id);
      }

      toast({
        title: 'Success',
        description: 'RESPA compliance form saved successfully',
      });
    } catch (error) {
      console.error('Error saving RESPA compliance:', error);
      toast({
        title: 'Error',
        description: 'Failed to save RESPA compliance form',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast({
          title: 'Error',
          description: 'Please allow popups to export PDF',
          variant: 'destructive',
        });
        return;
      }

      const previousYear = formData.current_year - 1;
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>RESPA Compliance Form</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 40px;
                max-width: 800px;
                margin: 0 auto;
                line-height: 1.6;
              }
              h1 {
                text-align: center;
                margin-bottom: 40px;
                color: #333;
                font-size: 24px;
              }
              .question {
                margin-bottom: 30px;
                page-break-inside: avoid;
              }
              .question-text {
                font-weight: bold;
                margin-bottom: 10px;
                color: #333;
              }
              .answer {
                margin-left: 20px;
                padding: 10px;
                border-bottom: 1px solid #000;
                min-height: 30px;
              }
              .checkbox-row {
                display: flex;
                align-items: center;
                gap: 10px;
                margin-left: 20px;
              }
              .checkbox {
                width: 18px;
                height: 18px;
                border: 2px solid #000;
                display: inline-block;
                text-align: center;
                line-height: 18px;
                font-size: 14px;
              }
              .signature-section {
                margin-top: 60px;
                display: flex;
                gap: 40px;
                align-items: flex-end;
              }
              .signature-line {
                flex: 1;
                border-bottom: 2px solid #000;
                min-height: 40px;
                padding-bottom: 5px;
              }
              .signature-label {
                font-weight: bold;
                margin-bottom: 5px;
              }
              @media print {
                body { padding: 20px; }
              }
            </style>
          </head>
          <body>
            <h1>RESPA Compliance Form</h1>
            
            <div class="question">
              <div class="question-text">How many Notes/Contracts did you originate in ${previousYear}?</div>
              <div class="answer"></div>
            </div>

            <div class="question">
              <div class="question-text">How many had an interest rate of 9% or more?</div>
              <div class="answer"></div>
            </div>

            <div class="question">
              <div class="question-text">How many Notes/Contracts did you originate in ${formData.current_year}, including the Note you are looking to sell?</div>
              <div class="answer"></div>
            </div>

            <div class="question">
              <div class="question-text">How many had an interest rate of 9% or more?</div>
              <div class="answer"></div>
            </div>

            <div class="signature-section">
              <div style="flex: 1;">
                <div class="signature-label">Signed</div>
                <div class="signature-line"></div>
              </div>
              <div style="flex: 1;">
                <div class="signature-label">Date</div>
                <div class="signature-line"></div>
              </div>
            </div>

            <script>
              window.onload = function() {
                setTimeout(() => {
                  window.print();
                  window.close();
                }, 250);
              };
            </script>
          </body>
        </html>
      `);

      printWindow.document.close();
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to export PDF',
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          RESPA Compliance Form
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Previous Year: {formData.current_year - 1}</Label>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Current Year: {formData.current_year}</Label>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="previous_year_count">
              Notes/Contracts originated in {formData.current_year - 1}
            </Label>
            <Input
              id="previous_year_count"
              type="number"
              min="0"
              value={formData.previous_year_count}
              onChange={(e) =>
                setFormData({ ...formData, previous_year_count: parseInt(e.target.value) || 0 })
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="current_year_count">
              Notes/Contracts originated in {formData.current_year} (including this one)
            </Label>
            <Input
              id="current_year_count"
              type="number"
              min="0"
              value={formData.current_year_count}
              onChange={(e) =>
                setFormData({ ...formData, current_year_count: parseInt(e.target.value) || 0 })
              }
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="previous_year_high_interest">
              How many had 9%+ interest rate?
            </Label>
            <Input
              id="previous_year_high_interest"
              type="number"
              min="0"
              max={formData.previous_year_count}
              value={formData.previous_year_high_interest}
              onChange={(e) =>
                setFormData({ ...formData, previous_year_high_interest: parseInt(e.target.value) || 0 })
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="current_year_high_interest">
              How many had 9%+ interest rate?
            </Label>
            <Input
              id="current_year_high_interest"
              type="number"
              min="0"
              max={formData.current_year_count}
              value={formData.current_year_high_interest}
              onChange={(e) =>
                setFormData({ ...formData, current_year_high_interest: parseInt(e.target.value) || 0 })
              }
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="signed_date">Signed Date</Label>
          <Input
            id="signed_date"
            type="date"
            value={formData.signed_date}
            onChange={(e) => setFormData({ ...formData, signed_date: e.target.value })}
          />
        </div>

        <div className="flex gap-2 pt-4">
          <Button onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : existingId ? 'Update' : 'Save'}
          </Button>
          <Button onClick={handleExportPDF} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default RESPAComplianceForm;
